import cn from 'classnames';

import { defineTheme, type ThemeKeys } from 'js/utils/theme-helpers';

import Byline from 'components/byline/Byline';
import Heading from 'components/heading/Heading';
import LinkWrapper from 'components/link-wrapper/LinkWrapper';
import Published from 'components/published/Published';
import TagLinkList from 'components/tag-link-list/TagLinkList';
import TagList from 'components/tag-list/TagList';
import ListItemImage from 'components/list-item-image/ListItemImage';
import Image from 'components/image/Image';
import { imageSizes } from 'components/image/image-sizes';
import Metadata from 'components/metadata/Metadata';
import DocumentIcon from 'components/document-icon/DocumentIcon';
import EventImage from 'components/event-image/EventImage';

import { IArticleBlock, ListItemImage_Size } from 'source/NFR.types';

const themes = defineTheme({
  noMargin: 'article-block--theme-no-margin',
  withBorder: 'article-block--theme-with-border',
  squareImage: 'article-block--theme-square-image',
  sidebar: 'article-block--theme-sidebar',
  vertical: 'article-block--theme-vertical',
  publication: 'article-block--theme-publication'
});
export type ArticleBlockTheme = ThemeKeys<typeof themes>;

export interface ArticleBlockProps extends IArticleBlock {
  headingLevel?: number;
  theme?: ArticleBlockTheme | ArticleBlockTheme[];
}

const ArticleBlock: React.FC<ArticleBlockProps> = ({
  byline,
  headingLevel = 2,
  image,
  text,
  theme,
  title,
  published,
  linkTags,
  tags,
  usedInSidebar,
  metadata,
  icon,
  documentImage,
  eventImage
}) => {
  const { OuterLink, InnerLink } = LinkWrapper(true, title.url);
  const isPublication = theme === themes.publication;

  return (
    <OuterLink className={cn('article-block', themes(theme))} attributes={{ 'aria-label': title.text ?? '' }}>
      <div className={cn('article-block__content', themes(theme))} key={text}>
        {image && !isPublication && (
          <ListItemImage image={image} size={usedInSidebar ? ListItemImage_Size.SmallImage : undefined} />
        )}
        {(icon || documentImage || isPublication) && (
          <div className="article-block--event-image-container">
            {image && eventImage && <EventImage {...eventImage} image={image} greyFilter />}
            <div className="article-block--document-container">
              {icon && <DocumentIcon {...icon} size="xlarge" />}
              {documentImage && (
                <div className="article-block--document-image">
                  <Image {...imageSizes.articleBlockDocument} {...documentImage} />
                </div>
              )}
            </div>
          </div>
        )}
        <div className="article-block__text-container">
          {tags && <TagList {...tags} />}
          <InnerLink>
            {title.text && (
              <Heading level={headingLevel} className={cn('article-block--title', themes(theme))}>
                {title.text}
              </Heading>
            )}
          </InnerLink>
          {published && <Published {...published} />}
          {metadata && <Metadata {...metadata} />}
          {byline && <Byline theme="border" {...byline} />}
          {text && <p className="article-block--text">{text}</p>}
          {linkTags && <TagLinkList {...linkTags} />}
        </div>
      </div>
    </OuterLink>
  );
};

ArticleBlock.displayName = 'ArticleBlock';

export default ArticleBlock;
